import React from 'react';
import { Camera, Cpu, CheckCircle2, XCircle, Info, CalendarClock } from 'lucide-react';

const tl = (v) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(v || 0);

const GoalRow = ({ icon: Icon, label, sold, target, rate, ok, testId }) => (
  <div className="rounded-lg border p-3" data-testid={testId}>
    <div className="flex items-center justify-between mb-1.5">
      <span className="flex items-center gap-2 text-sm font-medium text-gray-700"><Icon className="w-4 h-4" />{label}</span>
      <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${ok ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`} data-testid={`${testId}-status`}>
        {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}{ok ? 'Tamamlandı' : 'Eksik'}
      </span>
    </div>
    <div className="flex items-baseline justify-between text-sm">
      <span className="text-gray-800"><b className="text-lg" data-testid={`${testId}-sold`}>{sold}</b> / {target} adet</span>
      <span className="text-gray-500">%{String(rate).replace('.', ',')}</span>
    </div>
    <div className="h-2 bg-gray-100 rounded-full mt-1.5 overflow-hidden">
      <div className={`h-full rounded-full transition-[width] duration-500 ${ok ? 'bg-green-500' : 'bg-red-400'}`} style={{ width: `${Math.min(rate, 100)}%` }} />
    </div>
  </div>
);

const DailyNeed = ({ a }) => {
  if (!a.days_left) return null;
  const rows = [['Kamera', a.camera_target - a.camera_sold], ['Cihaz', a.device_target - a.device_sold]].filter(([, n]) => n > 0);
  if (!rows.length) return null;
  return (
    <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid={`daily-need-${a.username}`}>
      <p className="flex items-center gap-1.5 font-semibold"><CalendarClock className="w-4 h-4" />Ay sonuna {a.days_left} gün kaldı</p>
      {rows.map(([label, n]) => (
        <p key={label} className="mt-1" data-testid={`daily-need-${label.toLowerCase()}-${a.username}`}>
          {label}: <b>{n} adet</b> eksik → günde en az <b>{Math.ceil(n / a.days_left)} adet</b> satış gerekli
        </p>
      ))}
    </div>
  );
};

export function ProductTargetPanel({ a }) {
  if (!a.product_target_rule) return null;
  const tier1 = a.achievement_rate >= 100 ? a.achieved_points * 0.15 : 0;
  const upperExtra = Math.max(a.normal_commission - tier1, 0);
  const met = a.camera_ok && a.device_ok;
  return (
    <div className="space-y-3" data-testid={`product-target-panel-${a.username}`}>
      <div className="grid grid-cols-1 gap-3">
        <GoalRow icon={Camera} label="Kamera Hedefi" sold={a.camera_sold} target={a.camera_target} rate={a.camera_rate} ok={a.camera_ok} testId={`camera-goal-${a.username}`} />
        <GoalRow icon={Cpu} label="Cihaz Hedefi" sold={a.device_sold} target={a.device_target} rate={a.device_rate} ok={a.device_ok} testId={`device-goal-${a.username}`} />
      </div>
      <DailyNeed a={a} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-lg p-4 bg-green-50 border-2 border-green-300" data-testid={`earned-prim-${a.username}`}>
          <p className="text-xs font-semibold text-green-700">Kazanılan Prim</p>
          <p className="text-2xl font-bold text-green-700 mt-1">{tl(a.commission)}</p>
          {met && upperExtra > 0 && <p className="text-xs text-green-700 mt-1">Üst barem ilave primi dahil: {tl(upperExtra)}</p>}
        </div>
        <div className={`rounded-lg p-4 border-2 ${a.lost_commission > 0 ? 'bg-red-50 border-red-300' : 'bg-gray-50 border-gray-200'}`} data-testid={`lost-prim-${a.username}`}>
          <p className={`text-xs font-semibold ${a.lost_commission > 0 ? 'text-red-700' : 'text-gray-500'}`}>Hedef Nedeniyle Kazanılamayan Prim</p>
          <p className={`text-2xl font-bold mt-1 ${a.lost_commission > 0 ? 'text-red-600' : 'text-gray-400'}`}>{tl(a.lost_commission)}</p>
        </div>
      </div>
      <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3 space-y-0.5" data-testid={`barem-info-${a.username}`}>
        <p>Normal hesaplanan barem: <b>{a.normal_barem ? `${a.normal_barem}. barem` : 'Barem yok'}</b> ({tl(a.normal_commission)})</p>
        <p>Hedef kontrolü sonrası uygulanan barem: <b>{a.applied_barem ? `${a.applied_barem}. barem` : 'Barem yok'}</b></p>
        <p>Nihai prim hakedişi: <b>{tl(a.commission)}</b></p>
        {a.barem_note && <p className="flex items-start gap-1 text-red-600 pt-1"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />{a.barem_note}</p>}
      </div>
    </div>
  );
}
